import { z } from "zod";
import {
  artifactRefSchema,
  CANONICAL_SCHEMA_VERSION,
} from "./artifacts";
import { approvalRecordSchema } from "./approvals";

export const RUN_STATUS_OPTIONS = [
  "CREATED",
  "DISCOVERING",
  "AWAITING_BRIEF_APPROVAL",
  "PLANNING",
  "AWAITING_OUTLINE_APPROVAL",
  "DESIGNING",
  "AWAITING_VISUAL_APPROVAL",
  "GENERATING",
  "RENDERING",
  "EVALUATING",
  "REPAIRING",
  "EXPORTING",
  "COMPLETED",
  "PAUSED",
  "CANCELLED",
  "FAILED",
  "NEEDS_ATTENTION",
  "SUPERSEDED",
] as const;

export const runStatusSchema = z.enum(RUN_STATUS_OPTIONS);

const identifierSchema = z.string().trim().min(1).max(200);
const timestampSchema = z.string().datetime({ offset: true });
const semverSchema = z
  .string()
  .regex(/^\d+\.\d+\.\d+$/, "Expected a semantic version");

export const runFailureSchema = z
  .object({
    code: z.string().trim().min(1),
    classification: z.enum([
      "TRANSIENT",
      "PERMANENT",
      "SEMANTIC",
      "PERMISSION",
      "BUDGET",
    ]),
    summary: z.string().trim().min(1),
    nodeId: identifierSchema.optional(),
    retryable: z.boolean(),
    occurredAt: timestampSchema,
  })
  .strict();

const nodeAttemptSchema = z
  .object({
    nodeId: identifierSchema,
    attempt: z.number().int().positive(),
    status: z.enum(["PENDING", "RUNNING", "SUCCEEDED", "FAILED"]),
    startedAt: timestampSchema.optional(),
    completedAt: timestampSchema.optional(),
  })
  .strict();

const slideStateSchema = z
  .object({
    slideId: identifierSchema,
    nodeId: identifierSchema,
    status: z.enum(["PENDING", "RUNNING", "SUCCEEDED", "FAILED", "STALE"]),
    artifactRefs: z.array(artifactRefSchema),
  })
  .strict();

const runStateBaseSchema = z.object({
  schemaVersion: z.literal(CANONICAL_SCHEMA_VERSION),
  graphVersion: semverSchema,
  projectId: identifierSchema,
  runId: identifierSchema,
  revisionId: identifierSchema,
  surface: z.enum(["web", "windows"]),
  actorId: identifierSchema,
  artifacts: z.array(artifactRefSchema),
  approvals: z.array(approvalRecordSchema),
  slideStates: z.array(slideStateSchema),
  budget: z
    .object({
      maxCostUsd: z.number().nonnegative(),
      spentCostUsd: z.number().nonnegative(),
      maxAttempts: z.number().int().positive(),
      usedAttempts: z.number().int().nonnegative(),
    })
    .strict(),
  attempts: z.array(nodeAttemptSchema),
  failures: z.array(runFailureSchema),
  versionLineage: z
    .object({
      models: z.record(z.string(), z.string()),
      prompts: z.record(z.string(), z.string()),
      capabilities: z.record(z.string(), z.string()),
      tools: z.record(z.string(), z.string()),
    })
    .strict(),
  updatedAt: timestampSchema,
});

const createdRunStateSchema = runStateBaseSchema
  .extend({
    status: z.literal("CREATED"),
    nextAction: z.object({ type: z.literal("START") }).strict(),
  })
  .strict();

const activeRunStateSchema = runStateBaseSchema
  .extend({
    status: z.enum([
      "DISCOVERING",
      "PLANNING",
      "DESIGNING",
      "GENERATING",
      "RENDERING",
      "EVALUATING",
      "REPAIRING",
      "EXPORTING",
    ]),
    currentNodeId: identifierSchema,
    nextAction: z.object({ type: z.literal("CONTINUE") }).strict(),
  })
  .strict();

const approvalArtifactSchema = (kind: "presentation_brief" | "storyline" | "theme_spec") =>
  artifactRefSchema.extend({ kind: z.literal(kind) });

const awaitingBriefApprovalSchema = runStateBaseSchema
  .extend({
    status: z.literal("AWAITING_BRIEF_APPROVAL"),
    pendingApproval: z
      .object({
        gate: z.literal("brief"),
        artifact: approvalArtifactSchema("presentation_brief"),
      })
      .strict(),
    nextAction: z
      .object({
        type: z.literal("REVIEW_APPROVAL"),
        gate: z.literal("brief"),
      })
      .strict(),
  })
  .strict();

const awaitingOutlineApprovalSchema = runStateBaseSchema
  .extend({
    status: z.literal("AWAITING_OUTLINE_APPROVAL"),
    pendingApproval: z
      .object({
        gate: z.literal("storyline"),
        artifact: approvalArtifactSchema("storyline"),
      })
      .strict(),
    nextAction: z
      .object({
        type: z.literal("REVIEW_APPROVAL"),
        gate: z.literal("storyline"),
      })
      .strict(),
  })
  .strict();

const awaitingVisualApprovalSchema = runStateBaseSchema
  .extend({
    status: z.literal("AWAITING_VISUAL_APPROVAL"),
    pendingApproval: z
      .object({
        gate: z.literal("visual_direction"),
        artifact: approvalArtifactSchema("theme_spec"),
      })
      .strict(),
    nextAction: z
      .object({
        type: z.literal("REVIEW_APPROVAL"),
        gate: z.literal("visual_direction"),
      })
      .strict(),
  })
  .strict();

const pausedRunStateSchema = runStateBaseSchema
  .extend({
    status: z.literal("PAUSED"),
    pausedFrom: runStatusSchema,
    pausedBy: identifierSchema,
    nextAction: z.object({ type: z.literal("RESUME") }).strict(),
  })
  .strict();

const needsAttentionRunStateSchema = runStateBaseSchema
  .extend({
    status: z.literal("NEEDS_ATTENTION"),
    blockingFailure: runFailureSchema,
    nextAction: z
      .object({
        type: z.enum(["RETRY_NODE", "CREATE_REVISION", "CANCEL"]),
        nodeId: identifierSchema.optional(),
      })
      .strict(),
  })
  .strict();

const completedRunStateSchema = runStateBaseSchema
  .extend({
    status: z.literal("COMPLETED"),
    completedAt: timestampSchema,
    deliveryArtifacts: z.array(artifactRefSchema).min(1),
    nextAction: z.object({ type: z.literal("NONE") }).strict(),
  })
  .strict();

const failedRunStateSchema = runStateBaseSchema
  .extend({
    status: z.literal("FAILED"),
    completedAt: timestampSchema,
    terminalFailure: runFailureSchema,
    nextAction: z.object({ type: z.literal("NONE") }).strict(),
  })
  .strict();

const cancelledRunStateSchema = runStateBaseSchema
  .extend({
    status: z.literal("CANCELLED"),
    completedAt: timestampSchema,
    cancelledBy: identifierSchema,
    nextAction: z.object({ type: z.literal("NONE") }).strict(),
  })
  .strict();

const supersededRunStateSchema = runStateBaseSchema
  .extend({
    status: z.literal("SUPERSEDED"),
    completedAt: timestampSchema,
    supersededByRevisionId: identifierSchema,
    nextAction: z.object({ type: z.literal("NONE") }).strict(),
  })
  .strict();

export const runStateSchema = z.discriminatedUnion("status", [
  createdRunStateSchema,
  activeRunStateSchema,
  awaitingBriefApprovalSchema,
  awaitingOutlineApprovalSchema,
  awaitingVisualApprovalSchema,
  pausedRunStateSchema,
  needsAttentionRunStateSchema,
  completedRunStateSchema,
  failedRunStateSchema,
  cancelledRunStateSchema,
  supersededRunStateSchema,
]);

export type RunStatus = z.infer<typeof runStatusSchema>;

const sideTransitions = [
  "PAUSED",
  "CANCELLED",
  "FAILED",
  "NEEDS_ATTENTION",
  "SUPERSEDED",
] as const satisfies readonly RunStatus[];

const allowedRunTransitions: Record<RunStatus, readonly RunStatus[]> = {
  CREATED: ["DISCOVERING", "CANCELLED"],
  DISCOVERING: ["AWAITING_BRIEF_APPROVAL", ...sideTransitions],
  AWAITING_BRIEF_APPROVAL: ["DISCOVERING", "PLANNING", "CANCELLED", "SUPERSEDED"],
  PLANNING: ["AWAITING_OUTLINE_APPROVAL", ...sideTransitions],
  AWAITING_OUTLINE_APPROVAL: ["PLANNING", "DESIGNING", "CANCELLED", "SUPERSEDED"],
  DESIGNING: ["AWAITING_VISUAL_APPROVAL", ...sideTransitions],
  AWAITING_VISUAL_APPROVAL: ["DESIGNING", "GENERATING", "CANCELLED", "SUPERSEDED"],
  GENERATING: ["RENDERING", "EVALUATING", ...sideTransitions],
  RENDERING: ["EVALUATING", ...sideTransitions],
  EVALUATING: ["REPAIRING", "EXPORTING", ...sideTransitions],
  REPAIRING: ["GENERATING", "RENDERING", "EVALUATING", ...sideTransitions],
  EXPORTING: ["COMPLETED", ...sideTransitions],
  PAUSED: [
    "DISCOVERING",
    "PLANNING",
    "DESIGNING",
    "GENERATING",
    "RENDERING",
    "EVALUATING",
    "REPAIRING",
    "EXPORTING",
    "CANCELLED",
    "SUPERSEDED",
  ],
  NEEDS_ATTENTION: [
    "DISCOVERING",
    "PLANNING",
    "DESIGNING",
    "GENERATING",
    "RENDERING",
    "EVALUATING",
    "REPAIRING",
    "EXPORTING",
    "CANCELLED",
    "SUPERSEDED",
  ],
  COMPLETED: [],
  CANCELLED: [],
  FAILED: [],
  SUPERSEDED: [],
};

export const runStateTransitionSchema = z
  .object({
    from: runStatusSchema,
    to: runStatusSchema,
  })
  .strict()
  .refine(({ from, to }) => allowedRunTransitions[from].includes(to), {
    message: "Run-state transition is not allowed",
    path: ["to"],
  });

export type RunFailure = z.infer<typeof runFailureSchema>;
export type RunState = z.infer<typeof runStateSchema>;
export type RunStateTransition = z.infer<typeof runStateTransitionSchema>;
